// Pure shaping helpers for the code-review ledger: how findings are grouped and
// ordered for a page, and what that page is allowed to claim about a repo. Kept
// dependency-free (no fetch, no React, no config) so they unit-test in isolation,
// matching the other modules in this directory.

import type { LedgerFinding, LedgerResponse, LedgerRun } from "../api/codeReview";

// Most serious first. severityRank reads a value's position here, so a severity
// the pr-reviewer adds later needs one entry in this list and nothing else.
const SEVERITY_ORDER: readonly string[] = ["BLOCK", "FLAG", "SUGGEST"];

/** Sort key for a severity: lower is more serious. Takes a string rather than
 *  LedgerSeverity because the value arrives over the wire, and a severity this
 *  build does not know ranks below every one it does, so an unrecognised word
 *  lands at the bottom of the page instead of above a BLOCK. */
export function severityRank(s: string): number {
  const i = SEVERITY_ORDER.indexOf(s);
  return i === -1 ? SEVERITY_ORDER.length : i;
}

/** One file's findings, worst first. */
export interface LedgerFileGroup {
  file: string;
  findings: LedgerFinding[];
}

/** Group a ledger's findings by file for display.
 *
 *  Files come back worst-finding-first with the path breaking a tie, so a BLOCK
 *  is never buried under a file whose name happens to sort earlier. Within a
 *  file the order is severity, and equal severities keep the order the
 *  pr-reviewer sent them in, because its own ordering is the only further
 *  ranking either end knows about. The path comparison is by code unit rather
 *  than localeCompare, which would order the same findings differently for
 *  viewers in different locales. */
export function groupByFile(items: LedgerFinding[]): LedgerFileGroup[] {
  const byFile = new Map<string, LedgerFinding[]>();
  for (const item of items) {
    const bucket = byFile.get(item.file);
    if (bucket) bucket.push(item);
    else byFile.set(item.file, [item]);
  }
  const groups: LedgerFileGroup[] = [];
  for (const [file, findings] of byFile) {
    // Array.prototype.sort is stable, which is what leaves equal severities in
    // arrival order. Each bucket is built here, so this never reorders the
    // caller's array.
    findings.sort((a, b) => severityRank(a.severity) - severityRank(b.severity));
    groups.push({ file, findings });
  }
  groups.sort((a, b) => {
    // findings[0] is the group's worst: a group exists only because an item
    // created it, and the bucket was just sorted.
    const bySeverity = severityRank(a.findings[0]!.severity) - severityRank(b.findings[0]!.severity);
    if (bySeverity !== 0) return bySeverity;
    return a.file < b.file ? -1 : a.file > b.file ? 1 : 0;
  });
  return groups;
}

/** What the page may say about a repo. `unavailable` means we could not tell,
 *  which is a different fact from every other value here.
 *
 *  `has-findings` and `clean` describe the REPO, not the page of items in hand.
 *  They read the repo-wide open counts, so a `status=fixed` read of a repo that
 *  has open findings still reports `has-findings` even though its `items` came
 *  back empty. A caller that wants to describe the page it actually fetched
 *  should count `items` and read `total`/`has_more` itself. */
export type LedgerState = "never-reviewed" | "clean" | "has-findings" | "unavailable";

/** The error Bob writes on a run row when the pass starts and clears when it
 *  reports back. */
export const UNFINISHED_RUN = "run did not finish";

/** The newest pass that finished. Bob's run rows are born errored, so on a Bob
 *  without `last_completed_run` only an unerrored `last_run` counts. */
export function lastCompletedRun(r: LedgerResponse): LedgerRun | null {
  if (r.last_completed_run !== undefined) return r.last_completed_run ?? null;
  return r.last_run && !r.last_run.error ? r.last_run : null;
}

function everReviewed(r: LedgerResponse): boolean {
  return typeof r.reviewed === "boolean" ? r.reviewed : lastCompletedRun(r) !== null;
}

/** The newest run has not reported back: a pass is running, or one stopped
 *  without saying so. */
export function passRunning(r: LedgerResponse): boolean {
  return r.last_run?.error === UNFINISHED_RUN;
}

/** Decide what a ledger answer establishes.
 *
 *  `never-reviewed` and `clean` look alike on the wire, so only a finished pass
 *  earns `clean`. The daemon answers 200 for its own failures, so its markers
 *  are read first. `counts.open` is repo-wide while `items` is filtered by
 *  status. */
export function ledgerState(r: LedgerResponse): LedgerState {
  if (!r.configured) return "unavailable";
  // `note` and `error` need a value because `error: null` means nothing went
  // wrong; `upstream` counts on presence because 0 is a value it could carry.
  if (r.note != null || r.upstream !== undefined || r.error != null) return "unavailable";
  const openCounts = r.counts?.open;
  const open = openCounts ? Object.values(openCounts).reduce((sum, n) => sum + n, 0) : 0;
  if (open > 0 || r.items.length > 0) return "has-findings";
  return everReviewed(r) ? "clean" : "never-reviewed";
}

/** One pull request's findings, worst first. `pr` is null for findings no
 *  pull request owns. */
export interface LedgerPrGroup {
  pr: number | null;
  findings: LedgerFinding[];
}

/** Whole and from 1 up: GitHub numbers pull requests from 1. */
function isPrNumber(n: number): boolean {
  return Number.isSafeInteger(n) && n > 0;
}

/** Which pull request a finding belongs to. Absent, 0 and anything else no
 *  pull request could carry read as null, so they join the unattributed bucket
 *  instead of printing "Pull request #0". */
function prOf(f: LedgerFinding): number | null {
  const pr: unknown = f.pr_number;
  return typeof pr === "number" && isPrNumber(pr) ? pr : null;
}

/** Group findings by the pull request that introduced them, worst first.
 *  Equal worst severities put the newest pull request first. The unattributed
 *  bucket ranks by its worst finding like the rest and loses only the tie. */
export function groupByPr(items: LedgerFinding[]): LedgerPrGroup[] {
  const byPr = new Map<number | null, LedgerFinding[]>();
  for (const item of items) {
    const pr = prOf(item);
    const bucket = byPr.get(pr);
    if (bucket) bucket.push(item);
    else byPr.set(pr, [item]);
  }
  const groups: LedgerPrGroup[] = [];
  for (const [pr, findings] of byPr) {
    // A stable sort keeps equal severities in the order Bob sent them.
    findings.sort((a, b) => severityRank(a.severity) - severityRank(b.severity));
    groups.push({ pr, findings });
  }
  groups.sort((a, b) => {
    const bySeverity = severityRank(a.findings[0]!.severity) - severityRank(b.findings[0]!.severity);
    if (bySeverity !== 0) return bySeverity;
    // Numeric: as strings "10" sorts before "2".
    if (a.pr !== null && b.pr !== null) return b.pr - a.pr;
    if (a.pr === null && b.pr === null) return 0;
    return a.pr === null ? 1 : -1;
  });
  return groups;
}

/** The finding a `?finding=<id>` link names, or null. */
export function findingFromSearch(search: string): number | null {
  const raw = new URLSearchParams(search).get("finding")?.trim();
  if (!raw || !/^\d+$/.test(raw)) return null;
  const id = Number(raw);
  return Number.isSafeInteger(id) && id > 0 ? id : null;
}

/** The pull request a `?pr=<number>` link names, or null for anything that is
 *  not a plain positive integer, so a mangled link focuses nothing. */
export function prFromSearch(search: string): number | null {
  const raw = new URLSearchParams(search).get("pr")?.trim();
  // Digits only: parseInt reads "12abc" as 12 and Number reads "" as 0.
  if (!raw || !/^\d+$/.test(raw)) return null;
  const pr = Number(raw);
  return isPrNumber(pr) ? pr : null;
}

/** Put one pull request's group first, leaving the others in order. A link
 *  is a starting point, so nothing is filtered out. */
export function focusPr(groups: LedgerPrGroup[], pr: number | null): LedgerPrGroup[] {
  if (pr === null) return groups;
  const i = groups.findIndex((g) => g.pr === pr);
  if (i <= 0) return groups;
  return [groups[i]!, ...groups.slice(0, i), ...groups.slice(i + 1)];
}

export type LedgerEventKind = "found" | "seen" | "regressed" | "fixed" | "dismissed";

/** One entry in a finding's history. A null field is rendered as absent. */
export interface LedgerEvent {
  kind: LedgerEventKind;
  /** The chip beside the node. */
  label: string;
  title: string;
  sha: string | null;
  /** A timestamp this event can honestly be dated from, or null. */
  at: string | null;
  note: string | null;
}

/** A string field off the wire, or null for anything that is not non-blank
 *  text: nothing on the way here reads inside an item. */
function text(v: unknown): string | null {
  return typeof v === "string" && v.trim() !== "" ? v : null;
}

/**
 * A finding's history, oldest first, built only from what the row carries.
 *
 * The row keeps two timestamps and neither is an event log. FOUND takes
 * `created_at`, and FIXED takes `updated_at` because the only write that leaves
 * a row fixed is the fix. Everything else is undated: a pass that sees a
 * dismissed defect again rewrites `updated_at`, and nothing records when a
 * sighting or a regression happened.
 */
export function ledgerTimeline(f: LedgerFinding): LedgerEvent[] {
  const events: LedgerEvent[] = [];
  const firstSha = text(f.first_seen_sha);
  const lastSha = text(f.last_seen_sha);
  const status = typeof f.status === "string" ? f.status : "";

  events.push({
    kind: "found",
    label: "FOUND",
    title: "Bob first saw this",
    sha: firstSha,
    at: text(f.created_at),
    note: null,
  });

  // Only when a later commit carried it: equal shas are one sighting.
  if (lastSha !== null && lastSha !== firstSha) {
    events.push({
      kind: "seen",
      label: "SEEN AGAIN",
      title: "Still there on a later commit",
      sha: lastSha,
      // `updated_at` is not a fallback: a Solved claim moves it too.
      at: text(f.last_seen_at),
      note: null,
    });
  }

  const regressed = f.regressed_count;
  if (typeof regressed === "number" && Number.isSafeInteger(regressed) && regressed > 0) {
    events.push({
      kind: "regressed",
      label: "CAME BACK",
      title: regressed === 1
        ? "Came back after a pass had closed it"
        : `Came back ${regressed} times after a pass had closed it`,
      sha: null,
      at: null,
      note: null,
    });
  }

  // A Solved claim leaves the finding open, so it earns no event.
  if (status === "fixed") {
    events.push({
      kind: "fixed",
      label: "FIXED",
      title: "A later pass checked and the defect was gone",
      sha: text(f.resolved_sha),
      at: text(f.updated_at),
      note: text(f.resolved_reason),
    });
  } else if (status === "dismissed") {
    const by = text(f.dismissed_by);
    events.push({
      kind: "dismissed",
      label: "DISMISSED",
      // Only an admin can dismiss, and a member is never sent who.
      title: by === null ? "An admin dismissed this" : `${by} dismissed this`,
      sha: null,
      at: null,
      note: text(f.dismissed_reason),
    });
  }

  return events;
}

/** The card's heading: the observable failure, else the message. */
export function headlineOf(f: LedgerFinding): string {
  return text(f.player_impact) ?? (typeof f.message === "string" ? f.message : "");
}

/** How each reproduction status renders. `predicted` steps were read off the
 *  code and nobody has run them. */
export const REPRO_STATUSES: Readonly<Record<string, { badge: string; note: string }>> = {
  predicted: { badge: "predicted", note: "derived from code, not executed" },
  verified: { badge: "verified", note: "executed in game, and it reproduced" },
  refuted: { badge: "refuted", note: "executed in game, and it did not reproduce" },
};

/** The finding's reproduction status, or null when there is none or this
 *  page does not know the value. */
export function reproStatusOf(f: LedgerFinding): string | null {
  const s = f.repro_status;
  return typeof s === "string" && Object.prototype.hasOwnProperty.call(REPRO_STATUSES, s) ? s : null;
}

// A step number the model wrote: "1." "2)" "3:" or "Step 4." followed by
// whitespace. The whitespace is required, so "2.5 blocks away" keeps its number.
const STEP_NUMBER = /^\s*(?:step\s*)?\d+\s*[.):]\s+/i;
const STEP_BULLET = /^\s*[-*•]\s+/;

/** The reproduction as steps. The page's ordered list numbers them, so the
 *  model's own numbers are stripped. */
export function reproSteps(raw: unknown): string[] {
  if (typeof raw !== "string") return [];
  return raw
    .split(/\r?\n/)
    .map((line) => line.replace(STEP_NUMBER, "").replace(STEP_BULLET, "").trim())
    .filter((line) => line !== "");
}

/** The repair size, capitalised, or null outside small, medium and large. */
export function effortOf(f: LedgerFinding): string | null {
  const e = f.effort;
  if (e === "small") return "Small";
  if (e === "medium") return "Medium";
  if (e === "large") return "Large";
  return null;
}

/** Append one ledger page to the rows already loaded, keeping the first copy:
 *  paging is by offset, so a row that moved between reads can arrive twice. */
export function mergeLedgerPages(loaded: readonly LedgerFinding[], next: readonly LedgerFinding[]): LedgerFinding[] {
  const seen = new Set(loaded.map((f) => f.id));
  const out = loaded.slice();
  for (const f of next) {
    if (seen.has(f.id)) continue;
    seen.add(f.id);
    out.push(f);
  }
  return out;
}
